import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export default function EditFollowUpDialog({ open, onOpenChange, decisionId, followUp, onUpdated }) {
  const { getAuthHeaders, API } = useAuth();
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState(new Date());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (followUp) {
      setDescription(followUp.description || '');
      try {
        setDueDate(parseISO(followUp.due_date));
      } catch {
        setDueDate(new Date());
      }
    }
  }, [followUp]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!description.trim()) {
      toast.error('Please enter a description');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.put(
        `${API}/decisions/${decisionId}/followups/${followUp.id}`,
        {
          description: description.trim(),
          due_date: format(dueDate, 'yyyy-MM-dd')
        },
        { headers: getAuthHeaders() }
      );
      
      onUpdated(response.data);
      onOpenChange(false);
      toast.success('Follow-up updated');
    } catch (error) {
      console.error('Error updating follow-up:', error);
      toast.error('Failed to update follow-up');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="edit-followup-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-medium">Edit Follow-up</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="edit-followup-description" className="text-sm tracking-wide text-muted-foreground uppercase">
              Action
            </Label>
            <Input
              id="edit-followup-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What needs to be done?"
              className="input-underline text-base py-3"
              data-testid="edit-followup-description-input"
            />
          </div>

          {/* Due Date */}
          <div className="space-y-2">
            <Label className="text-sm tracking-wide text-muted-foreground uppercase">
              Due Date
            </Label>
            <Popover modal={true}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !dueDate && "text-muted-foreground"
                  )}
                  data-testid="edit-followup-date-picker"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dueDate ? format(dueDate, 'PPP') : <span>Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 z-[100]" align="start" sideOffset={4}>
                <Calendar
                  mode="single"
                  selected={dueDate}
                  onSelect={(d) => d && setDueDate(d)}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              data-testid="cancel-edit-followup"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="rounded-sm"
              data-testid="submit-edit-followup"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
